import { useCallback } from "react";
import { _ACTIONS, _HTTP, _SWAL_ICONS } from "../utils/constants";
import { createToast } from "../utils/toast";
import { DataService } from "../services/DataService";

/**
 * Custom hook to manage budget data operations
 * @param {Object} options - Configuration options
 * @param {Function} options.setRows - Function to set rows state
 * @param {Function} options.onDataLoadingStart - Function to trigger loading state
 * @param {Function} options.onDataLoadingStop - Function to stop loading state
 * @param {Object} options.params - Query parameters
 * @param {Function} options.formDataToRow - Function to transform API data to row format
 * @returns {Object} - Methods for data operations
 */
export const useBudgetData = ({
  setRows,
  onDataLoadingStart,
  onDataLoadingStop,
  params,
  formDataToRow,
}) => {
  const dataService = new DataService();

  const loadData = useCallback(
    (route, queryParams = {}) => {
      setRows([]);
      onDataLoadingStart();

      const finalParams = { ...queryParams };

      // Add common parameters
      if (params.site) finalParams.site = params.site;
      if (params.activity) finalParams.activity = params.activity;
      if (params.fiscalYear) finalParams.fiscalYear = params.fiscalYear;
      if (params.version) finalParams.version = params.version;

      return new Promise((resolve) => {
        dataService.fetchData(route, finalParams, (data) => {
          const rowData = formDataToRow(data);
          setRows(rowData);
          onDataLoadingStop();
          resolve(data);
        });
      });
    },
    [params, setRows, onDataLoadingStart, onDataLoadingStop, formDataToRow],
  );

  const handleCellValueChanged = useCallback(
    (agParams, payloadGenerator, route, additionalCallback = null) => {
      const { colDef, data, newValue, node } = agParams;

      // Generate the payload using the provided generator function
      const payload = payloadGenerator({
        colDef,
        data,
        newValue,
        node,
        params,
      });
      const method = payload.id ? _ACTIONS.PATCH : _ACTIONS.POST;

      const url = payload.id ? `${route}/${payload.id}` : route;

      dataService.doRequest(method, url, payload, {}, async (response) => {
        if (response.status === _HTTP.created || response.status === _HTTP.ok) {
          createToast(_SWAL_ICONS.success, "Saved successfully");

          const responseData = await response.json();

          if (method === _ACTIONS.POST && responseData?.id) {
            // Update node data if needed (can be customized via additionalCallback)
            if (additionalCallback) {
              additionalCallback(node, data, responseData, agParams);
            } else {
              // Default behavior - update ID mapping
              const field = colDef.field;
              const month = parseInt(field.replace("months.", ""));

              const updatedData = {
                ...data,
                fiscal_month_id_mapping: {
                  ...data.fiscal_month_id_mapping,
                  [month]: responseData?.id,
                },
              };
              node.setData(updatedData);
            }
          }
        }
      });
      
    },
    [params],
  );

  return {
    loadData,
    handleCellValueChanged,
  };
};
